import React, { memo, useEffect } from 'react';
import { st, classes } from './Tag.st.css';
import CloseButton from '../CloseButton';
import Text from '../Text';
import { generateDataAttr } from '../utils/generateDataAttr';
import { TagProps, RemoveKey } from './Tag.types';
import {
  SIZE,
  SKIN,
  dataHooks,
  tagToTextSize,
  tagToTextWeight,
  validRemoveKeys,
} from './Tag.constants';
import deprecationLog from '../utils/deprecationLog';

// Must return undefined (not "undefinedpx") when maxWidth is unset —
// assigning an invalid string leaves a previously-set inline max-width
// in place, since the CSSOM rejects invalid values silently.
const formatPx = (maxWidth: TagProps['maxWidth']) =>
  maxWidth === undefined
    ? undefined
    : typeof maxWidth === 'string'
      ? maxWidth
      : `${maxWidth}px`;

const Tag = ({
  id,
  onClick,
  onRemove,
  maxWidth,
  dataHook,
  size = SIZE.small,
  theme = SKIN.standard, // TODO: add default value for skin once theme is removed
  removable = true,
  disabled,
  thumb,
  ellipsisProps,
  className,
  children,
  closeButtonRef,
  ...props
}: TagProps) => {
  const skin = props.skin || theme;

  useEffect(() => {
    if (theme) {
      deprecationLog(
        '<Tag/> - theme prop is deprecated and will be removed in next major version, please use skin instead',
      );
    }
  }, [theme]);

  const handleRemove = (
    event:
      | React.KeyboardEvent<HTMLButtonElement>
      | React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    onRemove?.(id);
  };

  const handleOnClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    onClick?.(id, event);
  };

  const handleRemoveKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
  ) => {
    if (validRemoveKeys.includes(event.key as RemoveKey)) {
      handleRemove(event);
    }
  };

  const clickable = !!onClick;
  const hoverable = !disabled && !!onClick;

  return (
    <span
      className={st(
        classes.root,
        {
          withRemoveButton: removable,
          withThumb: !!thumb,
          disabled,
          clickable,
          hoverable,
          size,
          skin,
        },
        className,
      )}
      {...generateDataAttr({ size, disabled, skin, hoverable, clickable }, [
        'size',
        'disabled',
        'skin',
        'hoverable',
        'clickable',
      ])}
      data-hook={dataHook}
      id={id}
      onClick={handleOnClick}
      style={{ maxWidth: formatPx(maxWidth) }}
    >
      {thumb && (
        <span data-hook={dataHooks.thumb} className={classes.thumb}>
          {thumb}
        </span>
      )}

      <Text
        skin={disabled ? 'disabled' : 'standard'}
        light={!disabled && skin === 'dark'}
        secondary={skin !== 'dark'}
        ellipsis
        tooltipProps={{ flip: true, fixed: false, ...ellipsisProps }}
        size={tagToTextSize[size]}
        weight={tagToTextWeight[size]}
        className={classes.text}
        dataHook={dataHooks.text}
      >
        {children}
      </Text>

      {removable && (
        <CloseButton
          ref={closeButtonRef}
          size={size === 'large' ? 'medium' : 'small'}
          skin={skin === 'dark' ? 'light' : 'dark'}
          disabled={disabled}
          dataHook={dataHooks.removeButton}
          className={classes.removeButton}
          onClick={handleRemove}
          onKeyDown={handleRemoveKeyDown}
        />
      )}
    </span>
  );
};

Tag.displayName = 'Tag';

export default memo(Tag);
